import { useSyncExternalStore } from 'react';

/**
 * The signed-in user's answer to analytics cookies. Clarity never starts without
 * a yes: Microsoft requires consent for EEA, UK and Swiss visitors, and the
 * privacy policy promises it to everyone.
 */
const CONSENT_KEY = 'discovr_cookie_consent';
const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;
const CLARITY_COOKIES = ['_clck', '_clsk'];

const listeners = new Set<() => void>();
// Holds the answer for this page when the browser refuses storage.
let unsavedAnswer: boolean | null = null;

declare global {
  interface Window {
    clarity?: unknown;
  }
}

function clarityIsRunning(): boolean {
  return typeof window.clarity === 'function';
}

/** True or false once answered; null when unanswered, expired or unreadable. */
export function readClarityConsent(now: number = Date.now()): boolean | null {
  if (unsavedAnswer !== null) return unsavedAnswer;
  try {
    const stored = JSON.parse(localStorage.getItem(CONSENT_KEY) ?? 'null') as {
      version?: number;
      savedAt?: number;
      analytics?: boolean;
    } | null;
    if (stored?.version !== CONSENT_VERSION || typeof stored.savedAt !== 'number') return null;
    if (now - stored.savedAt >= CONSENT_MAX_AGE_MS) return null;
    return stored.analytics === true;
  } catch {
    return null;
  }
}

function clearClarityData(): void {
  const labels = window.location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < labels.length - 1; i += 1) {
    domains.push(`; domain=.${labels.slice(i).join('.')}`);
  }
  for (const name of CLARITY_COOKIES) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
    }
  }
  try {
    sessionStorage.removeItem('_cltk');
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

export function setClarityConsent(allowed: boolean): void {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ version: CONSENT_VERSION, savedAt: Date.now(), analytics: allowed }),
    );
    unsavedAnswer = null;
  } catch {
    unsavedAnswer = allowed;
  }
  if (!allowed) {
    // Whatever the earlier answer was: an expired one reads as null, but its
    // cookies, and a Clarity started before it expired, can outlive it.
    clearClarityData();
    // Clarity can't be stopped once running, so the page starts again without it.
    if (clarityIsRunning()) {
      window.location.reload();
      return;
    }
  }
  listeners.forEach((listener) => listener());
}

function onOtherTabAnswer(event: StorageEvent): void {
  if (event.key !== CONSENT_KEY) return;
  if (readClarityConsent() !== true && clarityIsRunning()) {
    window.location.reload();
    return;
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener('storage', onOtherTabAnswer);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onOtherTabAnswer);
  };
}

export function useClarityConsent(): boolean | null {
  return useSyncExternalStore(subscribe, () => readClarityConsent());
}
